import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { useThemeV81 } from '../context/ThemeV81';
import { ReactComponent as TranslateIcon } from '../assets/icons/lucide-languages.svg';
import './HeaderMenusV108.css';

const hoverQuery = '(hover: hover) and (pointer: fine)';
const enabledItems = (menu) =>
  Array.from(
    menu?.querySelectorAll(
      '[role^="menuitem"]:not(:disabled):not([aria-disabled="true"])'
    ) || []
  );

function HeaderMenuV108({
  id,
  label,
  icon,
  name,
  open,
  onOpen,
  onClose,
  children,
}) {
  const container = useRef(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const closeTimer = useRef(null);
  const focusEdge = useRef(null);
  const openedBy = useRef(null);
  const [offset, setOffset] = useState(0);
  const cancelClose = () => clearTimeout(closeTimer.current);
  const close = (restoreFocus = false) => {
    cancelClose();
    onClose();
    if (restoreFocus) trigger.current?.focus();
  };
  const show = (origin, edge = null) => {
    cancelClose();
    openedBy.current = origin;
    focusEdge.current = edge;
    onOpen();
    if (open && edge) focusItem(edge);
  };
  const focusItem = (edge) => {
    const items = enabledItems(menu.current);
    (edge === 'last' ? items.at(-1) : items[0])?.focus();
    focusEdge.current = null;
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (!open) return undefined;
    const outside = (event) => {
      if (!container.current?.contains(event.target)) onClose();
    };
    const escape = (event) => {
      if (event.key !== 'Escape') return;
      onClose();
      if (container.current?.contains(document.activeElement)) {
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const position = () => {
      const anchor = trigger.current.getBoundingClientRect();
      const width = menu.current.getBoundingClientRect().width;
      const left = Math.max(
        12,
        Math.min(anchor.right - width, window.innerWidth - width - 12)
      );
      setOffset(left - (anchor.right - width));
    };
    position();
    if (focusEdge.current) focusItem(focusEdge.current);
    window.addEventListener('resize', position);
    return () => window.removeEventListener('resize', position);
  }, [open]);

  const onKeyDown = (event) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    if (event.target === trigger.current) {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        show('keyboard', event.key === 'ArrowUp' ? 'last' : 'first');
      }
      return;
    }
    if (event.key === 'Tab') {
      close(true);
      return;
    }
    const items = enabledItems(menu.current);
    const index = items.indexOf(document.activeElement);
    const next = {
      ArrowDown: (index + 1) % items.length,
      ArrowUp: (index - 1 + items.length) % items.length,
      Home: 0,
      End: items.length - 1,
    }[event.key];
    if (next !== undefined) {
      event.preventDefault();
      items[next]?.focus();
    }
  };

  return (
    <div
      ref={container}
      className='ac-header-menu-v108'
      data-menu={id}
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
      onPointerEnter={(event) => {
        cancelClose();
        if (
          event.pointerType === 'mouse' &&
          window.matchMedia?.(hoverQuery).matches
        ) {
          show('hover');
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'mouse') return;
        cancelClose();
        closeTimer.current = setTimeout(() => {
          if (!menu.current?.contains(document.activeElement)) onClose();
        }, 180);
      }}
    >
      <button
        ref={trigger}
        id={`header-${id}-trigger-v108`}
        type='button'
        className={
          'ac-header-trigger-v108' + (name ? ' ac-account-trigger-v108' : '')
        }
        aria-label={label}
        title={label}
        aria-haspopup='menu'
        aria-expanded={open}
        aria-controls={open ? `header-${id}-menu-v108` : undefined}
        onClick={(event) => {
          if (open && openedBy.current !== 'hover') {
            close();
          } else {
            show(event.detail === 0 ? 'keyboard' : 'click', 'first');
          }
        }}
      >
        <span className='ac-header-icon-slot-v108' aria-hidden='true'>
          {id === 'language' ? (
            <TranslateIcon focusable='false' />
          ) : (
            <Icon name={icon} />
          )}
        </span>
        {name && <span className='ac-account-name-v108'>{name}</span>}
      </button>
      {open && (
        <div
          ref={menu}
          id={`header-${id}-menu-v108`}
          className='ac-header-popup-v108'
          role='menu'
          aria-labelledby={`header-${id}-trigger-v108`}
          style={{ transform: `translateX(${offset}px)` }}
        >
          {name && <div className='ac-account-heading-v108'>{name}</div>}
          {children(() => close(true))}
        </div>
      )}
    </div>
  );
}

export default function HeaderMenusV108({ user, loggingOut, onLogout }) {
  const { t, i18n } = useTranslation();
  const { themeMode, resolvedTheme, setThemeMode } = useThemeV81();
  const { pathname } = useLocation();
  const [active, setActive] = useState(null);
  useEffect(() => setActive(null), [pathname, user]);
  const name = user?.display_name || user?.username || t('header.user');
  const themeIcon =
    themeMode === 'system'
      ? 'desktop'
      : resolvedTheme === 'dark'
      ? 'moon outline'
      : 'sun outline';
  const themes = [
    ['light', 'sun outline'],
    ['dark', 'moon outline'],
    ['system', 'desktop'],
  ];
  const languages = [
    ['zh', '\u4e2d\u6587'],
    ['en', 'English'],
  ];
  const currentLanguage = i18n.language?.startsWith('zh') ? 'zh' : 'en';
  const state = (id) => ({
    id,
    open: active === id,
    onOpen: () => setActive(id),
    onClose: () => setActive((current) => (current === id ? null : current)),
  });
  return (
    <>
      <HeaderMenuV108
        {...state('theme')}
        label={`${t('theme.label')}: ${t(`theme.${themeMode}`)}`}
        icon={themeIcon}
      >
        {(close) =>
          themes.map(([mode, icon]) => (
            <button
              key={mode}
              type='button'
              role='menuitemradio'
              aria-checked={themeMode === mode}
              tabIndex={-1}
              onClick={() => {
                setThemeMode(mode);
                close();
              }}
            >
              <Icon name={icon} aria-hidden='true' />
              <span>{t(`theme.${mode}`)}</span>
              {themeMode === mode && (
                <Icon name='check' className='ac-menu-check-v108' />
              )}
            </button>
          ))
        }
      </HeaderMenuV108>
      <HeaderMenuV108 {...state('language')} label={t('v9.language')}>
        {(close) =>
          languages.map(([language, text]) => (
            <button
              key={language}
              type='button'
              role='menuitemradio'
              aria-checked={currentLanguage === language}
              lang={language}
              tabIndex={-1}
              onClick={() => {
                i18n.changeLanguage(language);
                close();
              }}
            >
              <span>{text}</span>
              {currentLanguage === language && (
                <Icon name='check' className='ac-menu-check-v108' />
              )}
            </button>
          ))
        }
      </HeaderMenuV108>
      {user && (
        <HeaderMenuV108
          {...state('account')}
          label={name}
          name={name}
          icon='user circle outline'
        >
          {(close) => (
            <>
              <Link to={user.account_kind_v110 === 'staff' ? '/staff/security' : '/setting'} role='menuitem' tabIndex={-1} onClick={close}>
                <Icon name='setting' aria-hidden='true' />
                <span>{t('header.user_settings')}</span>
              </Link>
              <button
                type='button'
                role='menuitem'
                tabIndex={-1}
                aria-disabled={loggingOut}
                aria-busy={loggingOut}
                onClick={() => {
                  if (!loggingOut) onLogout();
                }}
              >
                <Icon
                  name={loggingOut ? 'spinner' : 'sign-out alternate'}
                  loading={loggingOut}
                  aria-hidden='true'
                />
                <span>{t('header.logout')}</span>
              </button>
            </>
          )}
        </HeaderMenuV108>
      )}
    </>
  );
}
